import React, { useEffect, useState } from "react";
import { createNode } from "../../services/operations/nodeAPI";
import { getEmployeeAssignedTask } from "../../services/operations/adminAPI";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {Link} from "react-router-dom"
import { markCompletedDrawing } from "../../services/operations/drawingsheetAPI";
import { 
  FileText, 
  Users, 
  Plus, 
  Eye, 
  CheckCircle, 
  Clock, 
  Image as ImageIcon, 
  Loader2,
  ArrowLeft,
  Target,
  Activity
} from "lucide-react";

const TaskAssigned = () => {
  const { user } = useSelector((state) => state.profile);
  const navigate = useNavigate();
  const [assignedDrawings, setAssignedDrawings] = useState([]);
  const [designIntentInputs, setDesignIntentInputs] = useState({});
  const [nodeNumberInputs, setNodeNumberInputs] = useState({});
  const [loadingNodeId, setLoadingNodeId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await getEmployeeAssignedTask(user.id);
      if (response.success) {
        setAssignedDrawings(response.data || []);
        const nodeNumbers = {};
        response.data.forEach((drawing) => {
          const lastNodeNumber =
            drawing.nodes && drawing.nodes.length > 0
              ? Math.max(...drawing.nodes.map((n) => n.nodeNumber))
              : 0;
          nodeNumbers[drawing._id] = lastNodeNumber + 1;
        });
        setNodeNumberInputs(nodeNumbers);
      } else {
        toast.error(response.message || "Failed to fetch assigned drawings");
      }
    } catch (error) {
      toast.error("Error fetching assigned tasks");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && user.id) {
      fetchTask();
    }
  }, [user]);

  const handleDesignIntentChange = (drawingId, value) => {
    setDesignIntentInputs((prev) => ({
      ...prev,
      [drawingId]: value,
    }));
  };

  const handleNodeNumberChange = (drawingId, value) => {
    setNodeNumberInputs((prev) => ({
      ...prev,
      [drawingId]: value,
    }));
  };

  const handleMarkAsCompleted = async (drawingId) => {
    try {
      const response = await markCompletedDrawing(drawingId);
      if (response.success) {
        toast.success(response.message);
        fetchTask();
      } else {
        toast.error(response.message || "Failed to mark as completed");
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  const handleCreateNode = async (drawingSheet) => {
    const designIntent = designIntentInputs[drawingSheet._id];
    const nodeNumber = nodeNumberInputs[drawingSheet._id];

    if (!designIntent) return toast.error("Please enter a design intent");
    if (!nodeNumber || nodeNumber < 1)
      return toast.error("Enter a valid node number");

    const payload = {
      drawingSheetId: drawingSheet._id,
      designIntent,
      nodeNumber,
      createdBy: user.id,
    };

    try {
      setLoadingNodeId(drawingSheet._id);
      const response = await createNode(payload);
      if (response.success) {
        toast.success("Node created successfully!");
        setDesignIntentInputs((prev) => ({
          ...prev,
          [drawingSheet._id]: "",
        }));
        fetchTask();
      } else {
        toast.error(response.message || "Failed to create node");
      }
    } catch (error) {
      toast.error("Something went wrong while creating node");
    } finally {
      setLoadingNodeId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="animate-spin h-12 w-12 text-blue-500" />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Modern Header */}
        <div className="flex justify-between items-center mb-12">
          <div className="flex flex-col">
          <div className="flex gap-2 items-center ">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Target className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ">
            Assigned Tasks
          </h1>
          </div>
          <p className="ml-2 text-slate-600 text-lg">Create nodes and manage your HAZOP assignments</p>
          </div>

          <Link
          to={`/dashboard/ai-hazop`}
          className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 font-semibold"
          >
            <Eye className="h-4 w-4" />
            <div className="">View AI Hazop</div>
          </Link>
        </div>
        

        <div className="space-y-8">
          {assignedDrawings.length > 0 ? (
            assignedDrawings.map((drawing) => {
              const hasNodes = drawing.nodes.length > 0;
              const nextNodeNumber = nodeNumberInputs[drawing._id];

              return (
                <div key={drawing._id} className="group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-blue-50/50 rounded-3xl"></div>
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-black/20 p-8 transition-all duration-500 hover:shadow-2xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Drawing Info */}
                      <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                            <FileText className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-slate-800">Drawing {drawing.drawingNumber}</h3>
                            <p className="text-slate-600">HAZOP Analysis Task</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="p-4 bg-emerald-50/80 backdrop-blur-sm rounded-xl border border-emerald-200/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Users className="h-4 w-4 text-emerald-600" />
                              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Chairman</span>
                            </div>
                            <p className="text-slate-800 font-semibold">{drawing.chairman.firstName} {drawing.chairman.lastName}</p>
                          </div>

                          <div className="p-4 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Users className="h-4 w-4 text-blue-600" />
                              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Scribe</span>
                            </div>
                            <p className="text-slate-800 font-semibold">{drawing.scribe.firstName} {drawing.scribe.lastName}</p>
                          </div>
                        </div>

                        <div className="p-4 bg-purple-50/80 backdrop-blur-sm rounded-xl border border-purple-200/50">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-purple-600" />
                            <span className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Team Members</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {drawing.team.map((member, idx) => (
                              <span key={idx} className="px-3 py-1 bg-white/80 backdrop-blur-sm text-slate-700 text-sm rounded-full font-medium shadow-sm border border-white/50">
                                {member.firstName} {member.lastName}
                              </span>
                            ))}
                          </div>
                        </div>

                        {hasNodes && (
                          <div className="p-4 bg-amber-50/80 backdrop-blur-sm rounded-xl border border-amber-200/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Activity className="h-4 w-4 text-amber-600" />
                              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Existing Nodes</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {drawing.nodes.map((node, idx) => (
                                <span key={idx} className="px-3 py-1 bg-white/80 backdrop-blur-sm text-amber-700 text-sm rounded-full font-medium shadow-sm border border-white/50">
                                  Node {node.nodeNumber}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Drawing Image */}
                      <div className="flex flex-col items-center justify-center">
                        {drawing.drawingImage ? (
                          <div className="relative group cursor-pointer">
                            <img
                              src={drawing.drawingImage}
                              alt={`Drawing ${drawing.drawingNumber}`}
                              className="max-w-full max-h-80 rounded-2xl shadow-xl border border-white/20 transition-all duration-300 group-hover:shadow-2xl group-hover:scale-105"
                              onClick={() => setSelectedImage(drawing.drawingImage)}
                            />
                            
                          </div>
                        ) : (
                          <div className="w-full h-80 bg-gradient-to-br from-slate-100 to-gray-200 rounded-2xl flex items-center justify-center shadow-xl border border-white/20">
                            <div className="text-center">
                              <ImageIcon className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                              <p className="text-slate-500">No image available</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Node Creation Form */}
                    <div className="mt-8 p-6 bg-white/50 backdrop-blur-sm rounded-2xl border border-black/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-slate-700 font-semibold">
                            <Target className="h-4 w-4 text-blue-500" />
                            Node Number
                          </label>
                          <input
                            type="number"
                            className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300"
                            value={nextNodeNumber || ""}
                            onChange={(e) => handleNodeNumberChange(drawing._id, e.target.value)}
                            min={1}
                            placeholder="Enter node number"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-slate-700 font-semibold">
                            <FileText className="h-4 w-4 text-emerald-500" />
                            Design Intent
                          </label>
                          <textarea
                            className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 shadow-lg transition-all duration-300 resize-none"
                            placeholder="Enter Design Intent"
                            value={designIntentInputs[drawing._id] || ""}
                            onChange={(e) => handleDesignIntentChange(drawing._id, e.target.value)}
                            rows={4}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-4">
                        <button
                          onClick={() => handleCreateNode(drawing)}
                          disabled={loadingNodeId === drawing._id}
                          className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 text-white py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed font-semibold"
                        >
                          {loadingNodeId === drawing._id ? (
                            <>
                              <Loader2 className="animate-spin h-5 w-5" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Plus className="h-5 w-5" />
                              {hasNodes ? `Add Node ${nextNodeNumber}` : "Create Node 1"}
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => navigate(`/dashboard/employee/view-nodes/${drawing._id}`)}
                          className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 font-semibold"
                        >
                          <Eye className="h-5 w-5" />
                          View Nodes
                        </button>

                        <button
                          onClick={() => handleMarkAsCompleted(drawing._id)}
                          disabled={drawing.status === "Completed"}
                          className={`${
                            drawing.status === "Completed"
                              ? "bg-gradient-to-r from-emerald-400 to-green-500 cursor-default"
                              : "bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transform hover:scale-105"
                          } text-white py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 font-semibold`}
                        >
                          <CheckCircle className="h-5 w-5" />
                          {drawing.status === "Completed" ? "Completed" : "Mark as Completed"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-20">
              <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
                <Clock className="h-16 w-16 text-slate-400 mx-auto" />
              </div>
              <p className="text-slate-500 text-xl mb-2">No tasks assigned yet.</p>
              <p className="text-slate-400">Check back later for new assignments</p>
            </div>
          )}

          <button
            onClick={() => navigate("/dashboard/employee")}
            className="w-full bg-gradient-to-r from-slate-600 to-gray-700 hover:from-slate-700 hover:to-gray-800 text-white py-4 px-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 font-bold text-lg transform hover:scale-[1.02]"
          >
            <ArrowLeft className="h-6 w-6" />
            Back to Dashboard
          </button>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="relative max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl">
              <img
                src={selectedImage}
                alt="Full Size Drawing"
                className="max-w-full max-h-full object-contain rounded-2xl"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 p-3 bg-red-500/90 backdrop-blur-sm text-white rounded-full hover:bg-red-600 transition-all duration-300 shadow-lg hover:scale-110"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskAssigned;